<template>
  <div class="aywa-container">
    <div class="pro-form-card">
      <div class="pro-form-header">
        <div class="header-left">
          <v-icon class="primary-color-assuraf mr-2">two_wheeler</v-icon>
          <span class="badge">Assurance 2 roues</span>
        </div>
        <div v-if="agentCode" class="header-right">
          <div class="agent-badge" :class="{ 'is-valid': isAgentValid }">
            <i class="fas" :class="isAgentValid ? 'fa-user-check' : 'fa-user-tie'"></i>
            <span v-if="isAgentValid">
              Agent : <strong>{{ agentInfo.prenom }} {{ agentInfo.nom }}</strong> ({{ agentCode }})
            </span>
            <span v-else
              >Code Agent : <strong>{{ agentCode }}</strong></span
            >
          </div>
        </div>
      </div>

      <!-- Bloqueur : codeAgent dans l'URL mais invalide -->
      <div v-if="isAgentBlocked" class="agent-blocked-screen">
        <i class="fas fa-user-times agent-blocked-icon"></i>
        <h2>Code agent introuvable</h2>
        <p>
          Le code agent <strong>{{ agentCode }}</strong> n'est pas reconnu. Vous ne pouvez pas
          continuer ce parcours.
        </p>
        <a href="/aywa" class="btn-primary-modern">Acceder au parcours public</a>
      </div>

      <!-- Stepper iconographique -->
      <div v-if="!isAgentBlocked" class="stepper">
        <template v-for="(stepInfo, index) in steps">
          <div
            :key="`circle-${stepInfo.id}`"
            class="step-circle"
            :class="{
              'step-circle--inactive': step < stepInfo.id,
              'step-circle--done': step > stepInfo.id
            }"
          >
            <i class="fas" :class="step > stepInfo.id ? 'fa-check' : stepInfo.icon"></i>
            <span class="step-circle__label">{{ stepInfo.label }}</span>
          </div>
          <div
            v-if="index < steps.length - 1"
            :key="`line-${stepInfo.id}`"
            class="step-line"
            :class="{ 'step-line--active': step > stepInfo.id }"
          ></div>
        </template>
      </div>

      <!-- Step Content -->
      <div v-if="!isAgentBlocked" class="card-body">
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <p>{{ loadingMessage }}</p>
          </div>
        </div>

        <!-- Étape 1: Saisie des informations client -->
        <div v-if="step === 1">
          <p class="step-subtitle mt-5">Informations du Bénéficiaire</p>
          <div class="row">
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label for="number_tel" class="field-label"
                  >Numéro de téléphone <span class="required-asterisk">*</span></label
                >
                <div class="phone-input-wrapper">
                  <CountryCodeSelectAywa
                    v-model="countryCode"
                    class="country-select-aywa"
                  />
                  <input
                    id="number_tel"
                    v-model.trim="phoneNumber"
                    type="tel"
                    inputmode="numeric"
                    maxlength="25"
                    class="field-input phone-field-input"
                    :placeholder="phonePlaceholder"
                    name="lead_agent_phone"
                    @input="onPhoneInput"
                  />
                </div>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label class="field-label">Nom <span class="required-asterisk">*</span></label>
                <input
                  v-model="nom"
                  class="field-input"
                  placeholder="Saisissez le nom"
                  name="lead_agent_nom"
                />
              </div>
            </div>
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label class="field-label">Prénom <span class="required-asterisk">*</span></label>
                <input
                  v-model="prenom"
                  class="field-input"
                  placeholder="Saisissez le prénom"
                  name="lead_agent_prenom"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 2: Upload de la carte grise et validation des données OCR -->
        <div v-if="step === 2">
          <!-- OCR Mode: Section Upload -->
          <div v-if="vehicleMode === 'ocr' && !ocrSubmitted">
            <p class="step-subtitle">Upload recto et verso</p>
            <div class="fields">
              <div class="row">
                <div class="col-sm-6">
                  <div class="field-group mb-4">
                    <label class="field-label">Carte grise (recto)</label>
                    <div class="upload-box-wrapper">
                      <div v-if="!carteGriseRecto" class="upload-options">
                        <button
                          type="button"
                          class="btn-upload-opt camera"
                          @click="triggerUpload('recto', true)"
                        >
                          <i class="fas fa-camera"></i>
                          <span>Prendre une photo</span>
                        </button>
                        <button
                          type="button"
                          class="btn-upload-opt file"
                          @click="triggerUpload('recto', false)"
                        >
                          <i class="fas fa-file-upload"></i>
                          <span>Choisir un fichier</span>
                        </button>
                      </div>
                      <div v-else class="upload-preview">
                        <div class="file-info">
                          <i class="fas fa-check-circle text-success"></i>
                          <span class="file-name">{{ carteGriseRecto.name }}</span>
                        </div>
                        <button type="button" class="btn-remove-file" @click="removeFile('recto')">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                    <!-- Hidden inputs for file and camera -->
                    <input
                      type="file"
                      ref="fileInputRecto"
                      accept="image/*"
                      @change="handleFileUpload($event, 'recto')"
                      style="display: none"
                    />
                    <input
                      type="file"
                      ref="cameraInputRecto"
                      accept="image/*"
                      capture="environment"
                      @change="handleFileUpload($event, 'recto')"
                      style="display: none"
                    />
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="field-group mb-4">
                    <label class="field-label">Carte grise (verso)</label>
                    <div class="upload-box-wrapper">
                      <div v-if="!carteGriseVerso" class="upload-options">
                        <button
                          type="button"
                          class="btn-upload-opt camera"
                          @click="triggerUpload('verso', true)"
                        >
                          <i class="fas fa-camera"></i>
                          <span>Prendre une photo</span>
                        </button>
                        <button
                          type="button"
                          class="btn-upload-opt file"
                          @click="triggerUpload('verso', false)"
                        >
                          <i class="fas fa-file-upload"></i>
                          <span>Choisir un fichier</span>
                        </button>
                      </div>
                      <div v-else class="upload-preview">
                        <div class="file-info">
                          <i class="fas fa-check-circle text-success"></i>
                          <span class="file-name">{{ carteGriseVerso.name }}</span>
                        </div>
                        <button type="button" class="btn-remove-file" @click="removeFile('verso')">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                    <!-- Hidden inputs for file and camera -->
                    <input
                      type="file"
                      ref="fileInputVerso"
                      accept="image/*"
                      @change="handleFileUpload($event, 'verso')"
                      style="display: none"
                    />
                    <input
                      type="file"
                      ref="cameraInputVerso"
                      accept="image/*"
                      capture="environment"
                      @change="handleFileUpload($event, 'verso')"
                      style="display: none"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="ocrError" class="error-message" style="font-size: 14px">{{ ocrError }}</div>

            <!-- Bascule vers saisie manuelle -->
            <div class="mode-divider"><span>ou</span></div>
            <div class="mode-switch-cta">
              <p class="mode-switch-text">Ancienne carte grise ou CMC ?</p>
              <button type="button" class="btn-link-mode" @click="setVehicleMode('manual')">
                Saisie manuelle <i class="fas fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>

          <!-- Mode Manuel: en-tête et formulaire -->
          <div v-if="vehicleMode === 'manual' && !ocrSubmitted">
            <div class="mode-switch-cta-back">
              <p class="mode-switch-text">Vous disposez d'une carte grise (nouveau modèle) ?</p>
              <button type="button" class="btn-link-mode-back-cta" @click="setVehicleMode('ocr')">
                <i class="fas fa-arrow-left mr-1"></i> Saisie automatique
              </button>
            </div>
            <div class="mode-divider"><span>ou</span></div>
            <p class="step-subtitle text-center">Saisissez les informations de votre véhicule.</p>
          </div>

          <!-- Formulaire véhicule (mode manuel + édition après OCR/manuel) -->
          <div v-if="(vehicleMode === 'manual' && !ocrSubmitted) || (ocrSubmitted && isEditingOcr)">
            <!-- Identifiants & Véhicule -->
            <div class="fields">
              <!-- Radio: véhicule déjà immatriculé ? -->
              <div class="row">
                <div class="col-sm-12 text-center">
                  <label class="field-label mb-2 d-block text-center">
                    Véhicule déjà immatriculé ? <span class="required-asterisk">*</span>
                  </label>
                  <div class="radio-inline-group justify-content-center">
                    <label class="radio-inline-row">
                      <input
                        type="radio"
                        value="oui"
                        v-model="vehiculeImmatricule"
                        @change="onVehiculeImmatriculeChange"
                      />
                      <span>Oui</span>
                    </label>
                    <label class="radio-inline-row">
                      <input
                        type="radio"
                        value="non"
                        v-model="vehiculeImmatricule"
                        @change="onVehiculeImmatriculeChange"
                      />
                      <span>Non</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Ligne véhicule: Marque | Modèle | Immat/Châssis (+ Date si bénéficiaire = propriétaire) -->
              <div class="row vehicle-row mt-3">
                <div :class="vehicleColClass" class="marque-col-with-cta">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Marque <span class="required-asterisk">*</span>
                    </label>
                    <select
                      v-if="!isCustomVehicle"
                      :value="ocrData.marque"
                      @change="onMarqueSelect($event.target.value)"
                      class="field-select"
                      name="marque"
                    >
                      <option value="">—</option>
                      <option v-for="m in manualMarques" :key="m" :value="m">{{ m }}</option>
                      <option value="__autre__">Autre (non listé)</option>
                    </select>
                    <input
                      v-else
                      v-model="ocrData.marque"
                      placeholder="Saisissez la marque"
                      class="field-input"
                      name="marque_libre"
                    />
                  </div>
                  <div class="cta-below-marque" v-if="!isCustomVehicle">
                    <button
                      type="button"
                      class="btn-link-mode-back btn-link-inline btn-cta-nowrap"
                      @click="forceCustomVehicle"
                    >
                      Véhicule non listé ? <i class="fas fa-arrow-right ml-1"></i>
                    </button>
                  </div>
                  <div class="cta-below-marque" v-else>
                    <button
                      type="button"
                      class="btn-link-mode-back btn-link-inline btn-cta-nowrap"
                      @click="useStandardVehicle"
                    >
                      <i class="fas fa-arrow-left mr-1"></i> Retour à la liste
                    </button>
                  </div>
                </div>
                <div :class="vehicleColClass">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Modèle <span class="required-asterisk">*</span>
                    </label>
                    <select
                      v-if="!isCustomVehicle"
                      :value="ocrData.modele"
                      @change="onModeleSelect($event.target.value)"
                      class="field-select"
                      name="modele"
                      :disabled="!ocrData.marque"
                    >
                      <option value="">—</option>
                      <option v-for="md in manualModeles" :key="md" :value="md">{{ md }}</option>
                      <option value="__autre__">Autre (non listé)</option>
                    </select>
                    <input
                      v-else
                      v-model="ocrData.modele"
                      placeholder="Saisissez le modèle"
                      class="field-input"
                      name="modele_libre"
                    />
                  </div>
                </div>
                <div :class="vehicleColClass" v-if="vehiculeImmatricule === 'oui'">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Immatriculation <span class="required-asterisk">*</span>
                    </label>
                    <input
                      maxlength="10"
                      v-model="ocrData.immatriculation"
                      @input="validateImmatriculation"
                      placeholder="XX-XXX-XX ou XX-XXXX-XX"
                      class="field-input"
                      name="immatriculation_lead"
                    />
                  </div>
                </div>
                <div :class="vehicleColClass" v-else>
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Numéro de châssis <span class="required-asterisk">*</span>
                    </label>
                    <input
                      maxlength="17"
                      v-model="ocrData.numeroChassis"
                      @input="validateNumeroChassis"
                      placeholder="17 caractères (VIN)"
                      class="field-input"
                      name="numero_chassis"
                    />
                  </div>
                </div>
                <!-- Date sur la même ligne uniquement quand bénéficiaire = propriétaire (proprio inputs cachés) -->
                <div :class="vehicleColClass" v-if="!showProprietaireInputs">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Date de 1ère mise en circulation <span class="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      v-model="ocrData.dateCirculation"
                      :max="todayDate"
                      class="field-input"
                      name="date_circulation_compact"
                    />
                  </div>
                </div>
              </div>

              <!-- Ligne propriétaire & date: Nom | Prénom | Date (uniquement si proprio inputs visibles) -->
              <div class="row proprio-row" v-if="showProprietaireInputs">
                <div class="col-sm-4">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Nom du propriétaire <span class="required-asterisk">*</span>
                    </label>
                    <input v-model="ocrData.nom" class="field-input" name="proprio_nom" />
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Prénom du propriétaire <span class="required-asterisk">*</span>
                    </label>
                    <input v-model="ocrData.prenom" class="field-input" name="proprio_prenom" />
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="field-group field-default mb-4 mb-sm-0">
                    <label class="field-label">
                      Date de 1ère mise en circulation <span class="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      v-model="ocrData.dateCirculation"
                      :max="todayDate"
                      class="field-input"
                      name="date_circulation"
                    />
                  </div>
                </div>
              </div>

              <!-- Checkbox Propriétaire = Bénéficiaire au bas -->
              <div class="row proprio-checkbox-row" v-if="canUseSameAsBeneficiaire">
                <div class="col-sm-12">
                  <label class="checkbox-inline-row">
                    <input
                      type="checkbox"
                      v-model="proprietaireSameAsBeneficiaire"
                      @change="onProprietaireToggle"
                    />
                    <span>Le bénéficiaire est le propriétaire du véhicule</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Infos Véhicule (après OCR ou saisie manuelle) -->
          <div v-if="ocrSubmitted && !isEditingOcr">
            <p class="step-subtitle">Assurez-vous que les données sont correctes.</p>
            <div v-if="ocrError" class="error-message">
              <p>{{ ocrError }}</p>
              <button @click="resetOcr">Réessayer</button>
            </div>
            <div v-else>
              <!-- Vehicle Data Details View -->
              <div class="ocr-details-card">
                <div class="details-grid">
                  <div class="detail-item" v-if="proprietaireDisplay && proprietaireDisplay.trim()">
                    <span class="detail-label">Propriétaire</span>
                    <span class="detail-value">{{ proprietaireDisplay }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.immatriculation">
                    <span class="detail-label">Immatriculation</span>
                    <span class="detail-value">{{ ocrData.immatriculation }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.numeroChassis">
                    <span class="detail-label">N° de châssis</span>
                    <span class="detail-value">{{ ocrData.numeroChassis }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.marque">
                    <span class="detail-label">Marque</span>
                    <span class="detail-value">{{ ocrData.marque }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.modele">
                    <span class="detail-label">Modèle</span>
                    <span class="detail-value">{{ ocrData.modele }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.cylindree">
                    <span class="detail-label">Cylindrée</span>
                    <span class="detail-value">{{ cylindreeDisplay }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.puissanceFiscale">
                    <span class="detail-label">Puissance fiscale</span>
                    <span class="detail-value">{{ ocrData.puissanceFiscale }}</span>
                  </div>
                  <!-- <div class="detail-item" v-if="ocrData.nombrePlaces">
                    <span class="detail-label">Nombre de places</span>
                    <span class="detail-value">{{ ocrData.nombrePlaces }}</span>
                  </div> -->
                  <div class="detail-item" v-if="ocrData.carburant">
                    <span class="detail-label">Type de carburant</span>
                    <span class="detail-value">{{ ocrData.carburant }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.dateCirculation">
                    <span class="detail-label">Date 1ère mise en circulation</span>
                    <span class="detail-value">{{ formatDate(ocrData.dateCirculation) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 3: Validation (Choix durée + Devis + Paiement) -->
        <div v-if="step === 3">
          <!-- Section Choix de la durée (avant création du devis) -->
          <div>
            <p class="step-subtitle">Sélectionnez la période de votre contrat.</p>
            <div class="duration-options">
              <button
                v-for="option in durationOptionsToDisplay"
                :key="option.value"
                @click="selectDuration(option.value)"
                :class="['duration-btn', { selected: selectedDuration === option.value }]"
              >
                <div class="duration-content">
                  <span class="duration-label">{{ option.label }}</span>
                  <span class="duration-price">{{ option.price }} FCFA</span>
                </div>
              </button>
            </div>
          </div>
          <div class="date-effet-wrapper mb-4">
            <div class="field-group field-default">
              <label class="field-label">
                Date d'effet souhaitée <span class="required-asterisk">*</span>
              </label>
              <input
                type="date"
                v-model="customDateEffet"
                :min="minDateEffet"
                class="field-input"
                name="date_effet"
              />
              <span v-if="customDateEffet && customDateEffet < minDateEffet" class="error-text">
                La date d'effet ne peut pas être antérieure au {{ formatDate(minDateEffet) }}.
              </span>
            </div>
          </div>
        </div>

        <!-- Étape 4: Confirmation (Paiement + Résultat) - FUSIONNÉE AVEC STEP 3 -->
        <div v-if="step === 4">
          <!-- Section Résumé Devis + Paiement (après création du devis) -->
          <div
            v-if="devis && devis.devis && devis.devis.length > 0 && !contratDetails"
            class="checkout-card"
          >
            <div class="checkout-header">
              <i class="fas fa-motorcycle checkout-icon"></i>
              <h3 class="checkout-title">Récapitulatif de votre devis</h3>
            </div>
            <div class="checkout-rows">
              <div class="checkout-row">
                <span class="checkout-label">Numéro de devis</span>
                <span class="checkout-value">{{ devis.devis[0].numeroDevis }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Client</span>
                <span class="checkout-value"
                  >{{ devis.devis[0].proprietaire.prenom }}
                  {{ devis.devis[0].proprietaire.nom }}</span
                >
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Véhicule</span>
                <span class="checkout-value"
                  >{{ devis.devis[0].vehicules[0].marque }} {{ devis.devis[0].vehicules[0].model }}
                  <span class="checkout-sub"
                    >({{ devis.devis[0].vehicules[0].immatriculation }})</span
                  ></span
                >
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Période</span>
                <span class="checkout-value"
                  >{{ formatDate(devis.devis[0].dateEffet) }} →
                  {{ formatDate(devis.devis[0].dateEcheance) }}</span
                >
              </div>
            </div>

            <!-- Breakdown des frais -->
            <div class="checkout-breakdown">
              <div class="checkout-breakdown-row" v-if="devis.devis[0].prime_nette != null">
                <span class="checkout-breakdown-label">Prime nette</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.devis[0].prime_nette).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
              <div class="checkout-breakdown-row" v-if="devis.devis[0].taxe != null">
                <span class="checkout-breakdown-label">Taxe</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.devis[0].taxe).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
              <div
                class="checkout-breakdown-row"
                v-if="devis.devis[0].accessoires != null && Number(devis.devis[0].accessoires) > 0"
              >
                <span class="checkout-breakdown-label">Accessoires</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.devis[0].accessoires).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
              <div
                class="checkout-breakdown-row"
                v-if="devis.devis[0].fga != null && Number(devis.devis[0].fga) > 0"
              >
                <span class="checkout-breakdown-label">FGA</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.devis[0].fga).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
            </div>

            <div class="checkout-total">
              <span class="checkout-total-label">Total à payer</span>
              <span class="checkout-total-amount">
                {{ Number(devis.devis[0].prime_ttc).toLocaleString('fr-FR') }}
                <span class="checkout-total-currency">{{ devis.devis[0].devise || 'FCFA' }}</span>
              </span>
            </div>
          </div>

          <!-- Section Paiement -->
          <div class="payment-section mt-4" v-if="!contratDetails">
            <p class="payment-section-title">Choisissez votre moyen de paiement</p>
            <div class="payment-options">
              <div class="payment-buttons">
                <button @click="payWithWave" class="payment-btn wave">
                  <img src="../../assets/img/logoPaiement/wave.png" alt="Wave" />
                  <span class="payment-btn-label">Wave</span>
                </button>
                <button @click="payWithOrangeMoney" class="payment-btn om">
                  <img src="../../assets/img/logoPaiement/orange-money.png" alt="Orange Money" />
                  <span class="payment-btn-label">Orange Money</span>
                </button>
              </div>
            </div>
            <p class="payment-security-note">
              <i class="fas fa-shield-alt"></i> Paiement 100 % sécurisé
            </p>
          </div>

          <!-- Affiche la confirmation après paiement -->
          <div v-if="contratDetails" class="confirmation-summary">
            <p>Votre assurance moto a été validée avec succès.</p>
            <ul>
              <li>
                <strong>Numéro de police :</strong> {{ contratDetails.contrat.numero_police }}
              </li>
              <li>
                <strong>Date d'effet :</strong> {{ formatDate(contratDetails.contrat.dateEffet) }}
              </li>
              <li>
                <strong>Date d'échéance :</strong>
                {{ formatDate(contratDetails.contrat.dateEcheance) }}
              </li>
              <li><strong>Montant payé :</strong> {{ contratDetails.contrat.prime_ttc }} FCFA</li>
            </ul>
            <button
              v-if="!contratDetails.attestation"
              class="btn-refresh"
              @click="refreshContratDetails"
              :disabled="isLoading"
            >
              <i :class="['fas', 'fa-sync-alt', { 'fa-spin': isLoading }]"></i>
              Réactualiser pour obtenir le talon
            </button>
          </div>

          <div v-if="contratDetails && contratDetails.attestation" class="sharing-options">
            <div class="sharing-header">
              <h3>Partager le talon d'assurance</h3>
            </div>
            <div class="share-buttons-container">
              <button class="share-btn pdf" @click="share('pdf')">
                <i class="fas fa-file-pdf"></i> Télécharger l'attestation
              </button>
              <button class="share-btn pdf" @click="share('carte_brune')">
                <i class="fas fa-file-pdf"></i> Télécharger la carte brune
              </button>
              <button class="share-btn whatsapp" @click="share('whatsapp')">
                <i class="fab fa-whatsapp"></i> Partager sur WhatsApp
              </button>
              <button disabled class="share-btn sms" @click="share('sms')">
                <i class="fas fa-sms"></i> Envoyer par SMS
              </button>
            </div>
          </div>
          <!-- <div v-if="!contratDetails.attestation" class="error-message">
            L'attestation n'est pas disponible pour le moment. Veuillez réessayer plus tard.
          </div> -->
        </div>
      </div>

      <!-- Card Footer -->
      <div class="card-footer">
        <!-- Bouton pour passer à l'étape suivante -->
        <div class="actions step2-actions">
          <div class="actions-left">
            <button
              v-if="step > 1 && step !== 7"
              @click="prevStep"
              type="button"
              class="wizard-btn back-btn"
            >
              <v-icon>&#xe5c4;</v-icon>
              <span class="ml-2">{{
                $t('assur_lead.form_assur_lead.second_step.back_button_label')
              }}</span>
            </button>
          </div>
          <div class="actions-right">
            <button
              v-if="step === 2 && !ocrSubmitted && vehicleMode === 'ocr'"
              @click="submitOcr"
              :disabled="!areFilesUploaded || isLoading"
              class="btn-primary-modern"
            >
              <span>Analyser la carte grise</span>
            </button>

            <!-- Bouton Valider pour le mode manuel -->
            <button
              v-if="step === 2 && !ocrSubmitted && vehicleMode === 'manual'"
              @click="validateManualVehicle"
              :disabled="!isOcrDataValid"
              class="btn-primary-modern"
            >
              <span>Valider</span>
            </button>

            <!-- Bouton Re-uploader pour le step 2 (mode OCR uniquement) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr && vehicleMode === 'ocr'"
              @click="resetOcr"
              class="btn-outline-primary mr-3"
            >
              <i class="fas fa-upload"></i>
              <span class="ml-2">Re-uploader</span>
            </button>

            <!-- Bouton Modifier pour le step 2 (après OCR) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr"
              @click="toggleEditOcr"
              class="btn-outline-primary mr-3"
            >
              <i class="fas fa-pen"></i>
              <span class="ml-2">Modifier</span>
            </button>

            <!-- Boutons pour mode édition étape 2 -->
            <button
              v-if="step === 2 && ocrSubmitted && isEditingOcr"
              @click="cancelEditOcr"
              class="mr-3 wizard-btn back-btn"
            >
              <span>Annuler</span>
            </button>
            <button
              v-if="step === 2 && ocrSubmitted && isEditingOcr"
              @click="confirmEditOcr"
              :disabled="!isOcrDataValid"
              class="btn-primary-modern"
            >
              <span>Valider</span>
            </button>

            <!-- Bouton Suivant normal -->
            <button
              v-if="
                step < 4 &&
                step !== 2 &&
                step !== 3 &&
                (step !== 2 || !ocrSubmitted || !isEditingOcr)
              "
              @click="nextStep"
              :disabled="!isStepValid"
              class="btn-primary-modern"
            >
              <span>{{ $t('assur_lead.form_assur_lead.first_step.next_button_label') }}</span>
            </button>

            <!-- Bouton Suivant pour step 2 (après OCR) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr"
              @click="nextStep"
              class="btn-primary-modern"
            >
              <span>{{ $t('assur_lead.form_assur_lead.first_step.next_button_label') }}</span>
            </button>
            <!--          <button
            @click="nextStep"
            v-if="step < 6 && step !== 2 && step !== 5 && step !== 4"
            type="button"
            class="wizard-btn next-btn"
            :disabled="!isStepValid"
          >
            <span class="mr-2">{{
              $t('assur_lead.form_assur_lead.first_step.next_button_label')
            }}</span>
            <v-icon>&#xe5c8;</v-icon>
          </button>-->
          </div>
        </div>

        <!--        <button
           @click="nextStep"
           v-if="step < 6 && step !== 2 && step !== 5 && step !== 4"
           :disabled="!isStepValid"
           class="btn-primary-modern"
         >
           Suivant
         </button>-->
        <button
          @click="evaluateAndCreateDevis"
          v-if="step === 3"
          :disabled="
            !selectedDuration || !customDateEffet || customDateEffet < minDateEffet || isLoading
          "
          class="btn-primary-modern"
        >
          Obtenir le devis
        </button>
        <button @click="resubscribe" v-if="step === 3 && contratDetails" class="btn-primary-modern">
          Resouscrire
        </button>
      </div>
    </div>

    <!-- Modale d'avertissement assurance existante -->
    <div v-if="showInsuranceWarning" class="modal-overlay">
      <div class="modal-content warning-modal">
        <div class="modal-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Véhicule déjà assuré</h3>
        <p v-if="insuranceFound">
          Le véhicule ({{ insuranceFound.data.immatriculation }}) possède déjà une assurance active
          (N° <strong>{{ insuranceFound.data.attestationNumber }}</strong
          >)
          <span v-if="insuranceFound.marque"
            >pour un
            <strong>{{ insuranceFound.data.marque }} {{ insuranceFound.data.modele }}</strong></span
          >
          jusqu'au <strong>{{ formatDate(insuranceFound.data.dateEcheance) }}</strong
          >.
        </p>
        <p class="modal-question">
          Nous proposons de fixer la date d'effet au
          <strong>{{ formatDate(proposedDateEffet) }}</strong
          >. Valider ?
        </p>
        <div class="modal-actions">
          <button @click="cancelInsuranceWarning" class="btn-secondary-modern">Annuler</button>
          <button @click="confirmContinue" class="btn-primary-modern">Valider</button>
        </div>
      </div>
    </div>

    <!-- Modale de paiement Orange Money -->
    <div v-if="showOrangeMoneyModal" class="modal-overlay">
      <div class="modal-content">
        <h3>Paiement par Orange Money</h3>
        <div class="form-group">
          <label for="om-phone">Numéro de téléphone Orange</label>
          <input type="tel" id="om-phone" v-model="omPhoneNumber" placeholder="77xxxxxxx" />
        </div>
        <div class="form-group">
          <label for="om-otp">Code OTP (#144#391#)</label>
          <input type="text" id="om-otp" v-model="omOtp" placeholder="Code à 6 chiffres" />
        </div>
        <div class="modal-actions">
          <button @click="submitOrangeMoneyPayment" :disabled="!isOmFormValid || isLoading">
            Valider
          </button>
          <button @click="showOrangeMoneyModal = false" class="btn-secondary">Annuler</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CarteGriseOcrService from '@/services/CarteGriseOcrService';
import TiaKTiaKService from '@/services/TiaKTiaKService';
import Marque2roues from '@/common/2roues';
import Api from '@/services/Api';
import CountryCodeSelectAywa from '../../views/CountryCodeSelectAywa.vue';
import phoneDigitsMap from '../../common/phoneDigits.json';
import { sanitizePhoneNumber } from '@/common/sanitizePhoneNumber';

export default {
  name: 'AywaSubscription',
  components: {
    CountryCodeSelectAywa
  },
  props: {
    id_association: {
      type: String,
      required: false
    },
    agentCodeFromUrl: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      step: 1,
      agentCode: null,
      isAgentValid: false,
      agentInfo: {},
      agentValidationDone: false,
      // Step 1 data
      nom: '',
      prenom: '',
      phoneNumber: '',
      countryCode: '+221',
      // Step 2 data
      vehicleMode: 'ocr', // 'ocr' | 'manual'
      proprietaireSameAsBeneficiaire: false,
      isCustomVehicle: false,
      vehiculeImmatricule: 'oui', // 'oui' | 'non' (mode manuel uniquement)
      carteGriseRecto: null,
      carteGriseVerso: null,
      ocrSubmitted: false,
      // Step 3 data
      ocrData: {
        immatriculation: '',
        numeroChassis: '',
        nom: '',
        prenom: '',
        marque: '',
        modele: '',
        cylindree: '',
        carburant: '',
        puissanceFiscale: '',
        nombrePlaces: '2',
        dateCirculation: '',
        puissance_fiscale: null,
        nombre_places: null,
        type_carburant: '',
        date_premiere_mise_en_circulation: '',
        volumetrie2_roues: null
      },
      ocrError: null,
      // Step 4 data
      durationOptions: [
        { value: 1, label: '1 mois', price: 2000 },
        { value: 3, label: '3 mois', price: 5000 },
        { value: 6, label: '6 mois', price: 9000 },
        { value: 12, label: '12 mois', price: 18000 }
      ],
      selectedDuration: null,
      tarification: null,
      tarificationLoading: false,
      tarificationError: null,
      // Global state
      isLoading: false,
      isEditingOcr: false,
      loadingMessage: '',
      devis: null, // Pour stocker la réponse du devis
      contratDetails: null, // Pour stocker les détails du contrat
      pollingIntervalId: null,
      pollingTimeoutId: null,
      // Insurance check
      insuranceFound: null,
      showInsuranceWarning: false,
      pendingDevisCreation: false,
      customDateEffet: null,
      // Orange Money Modal
      showOrangeMoneyModal: false,
      omPhoneNumber: '',
      omOtp: '',
      // Stepper data
      steps: [
        { id: 1, label: 'Client', icon: 'fa-user' },
        { id: 2, label: 'Carte Grise', icon: 'fa-motorcycle' },
        { id: 3, label: 'Validation', icon: 'fa-clipboard-check' },
        { id: 4, label: 'Confirmation', icon: 'fa-check-circle' }
      ],
      // Select options (mirrors DevisAuto/auto.desktop.vue behavior)
      nombrePlacesItems: ['1', '2'],
      puissanceFiscaleItems: ['1', '2'],
      idAssociation: process.env.IDTIAKTIAKAGENT,
      ToastTabSucess: {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 4000,
        fitToScreen: true,
        type: 'success',
        className: 'custom-success-class'
      },
      ToastTabError: {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 4000,
        fitToScreen: true,
        type: 'error'
      }
    };
  },
  computed: {
    resolvedAssociationId() {
      const fromRoute =
        this.$route && this.$route.params ? this.$route.params.id_association : undefined;
      return this.id_association || fromRoute || null;
    },
    isAgentBlocked() {
      // Bloque le parcours si un codeAgent est present dans l'URL mais qu'il est invalide.
      // Le parcours public (/aywa sans code) reste accessible.
      return !!this.agentCode && this.agentValidationDone && !this.isAgentValid;
    },
    isStepValid() {
      switch (this.step) {
        case 1:
          return this.isStep1Valid;
        case 2:
          return this.ocrSubmitted && this.isOcrDataValid;
        case 3:
          return this.devis && this.selectedDuration !== null; // Validation (fusionné Durée + Devis & Paiement)
        case 4:
          return true; // Step 4 (Confirmation) n'a pas de validation classique
        default:
          return true;
      }
    },
    countryPhoneRule() {
      var cc = this.countryCode;
      var digits = phoneDigitsMap[cc];
      if (!digits) {
        var prefix = '+' + String(cc).replace(/[^0-9]/g, '').slice(0, 2);
        digits = phoneDigitsMap[prefix] || 9;
      }
      return { digits: digits };
    },
    phonePlaceholder() {
      var rule = this.countryPhoneRule;
      var d = rule.digits;
      var groups = [];
      for (var i = 0; i < d; i += 2) {
        var n = Math.min(2, d - i);
        groups.push(n === 2 ? 'XX' : 'X');
      }
      return groups.join(' ') + ' (' + d + ' chiffres)';
    },
    isStep1Valid() {
      var nom = this.nom ? String(this.nom).trim() : '';
      var prenom = this.prenom ? String(this.prenom).trim() : '';
      var phone = String(this.phoneNumber || '').replace(/[^0-9]/g, '');
      var rule = this.countryPhoneRule;
      if (phone.length > rule.digits && phone.length === rule.digits + 1 && phone.charAt(0) === '0') {
        phone = phone.slice(1);
      }
      return !!(nom && prenom) && phone.length === rule.digits;
    },
    areFilesUploaded() {
      return this.carteGriseRecto && this.carteGriseVerso;
    },
    isOmFormValid() {
      return this.omPhoneNumber.length > 8 && this.omOtp.length === 6;
    },
    durationOptionsToDisplay() {
      if (!this.tarification || !this.tarification.tarif) return this.durationOptions;
      var tarif = this.tarification.tarif || {};
      var priceByDuration = {};
      var keys = Object.keys(tarif);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var match = String(key).match(/\d+/);
        var duration = match ? parseInt(match[0], 10) : NaN;
        var entry = tarif[key];
        var primeTtc = entry && entry.prime_ttc != null ? Number(entry.prime_ttc) : NaN;
        if (!isNaN(duration) && duration > 0 && !isNaN(primeTtc)) {
          priceByDuration[duration] = Math.round(primeTtc);
        }
      }
      return (this.durationOptions || []).map((option) => {
        var mapped = priceByDuration[option.value];
        if (mapped == null) return option;
        return Object.assign({}, option, { price: mapped });
      });
    },
    proposedDateEffet() {
      if (
        !this.insuranceFound ||
        !this.insuranceFound.data ||
        !this.insuranceFound.data.dateEcheance
      ) {
        return '';
      }
      const nextDay = new Date(this.insuranceFound.data.dateEcheance);
      nextDay.setDate(nextDay.getDate() + 1);
      const month = String(nextDay.getMonth() + 1).padStart(2, '0');
      const day = String(nextDay.getDate()).padStart(2, '0');
      return nextDay.getFullYear() + '-' + month + '-' + day;
    },
    minDateEffet() {
      return this.proposedDateEffet || this.todayDate;
    },
    progressWidth() {
      const completedSteps = this.step - 1;
      const totalSteps = this.steps.length - 1;
      if (totalSteps === 0) return '0%';
      const percentage = (completedSteps / totalSteps) * 100;
      return `${percentage}%`;
    },
    canUseSameAsBeneficiaire() {
      var nom = this.nom ? String(this.nom).trim() : '';
      var prenom = this.prenom ? String(this.prenom).trim() : '';
      return !!(nom && prenom);
    },
    showProprietaireInputs() {
      return !this.canUseSameAsBeneficiaire || !this.proprietaireSameAsBeneficiaire;
    },
    vehicleColClass() {
      // Si proprio inputs visibles -> 3 champs (Marque|Modele|Immat) en col-sm-4
      // Sinon -> 4 champs (+ Date) sur la meme ligne en col-sm-3
      return this.showProprietaireInputs ? 'col-sm-4' : 'col-sm-3';
    },
    todayDate() {
      var d = new Date();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return d.getFullYear() + '-' + month + '-' + day;
    },
    isOcrDataValid() {
      var hasIdentifier;
      if (this.vehicleMode === 'manual') {
        hasIdentifier =
          this.vehiculeImmatricule === 'oui'
            ? !!this.ocrData.immatriculation
            : !!this.ocrData.numeroChassis;
      } else {
        hasIdentifier = !!(this.ocrData.immatriculation || this.ocrData.numeroChassis);
      }
      var hasProprietaire = !!(this.ocrData.nom && this.ocrData.prenom);
      const isDateValid =
        !!this.ocrData.dateCirculation && this.ocrData.dateCirculation <= this.todayDate;
      return (
        hasIdentifier &&
        !!this.ocrData.marque &&
        !!this.ocrData.modele &&
        isDateValid &&
        hasProprietaire
      );
    },
    proprietaireDisplay() {
      var full = ((this.ocrData.prenom || '') + ' ' + (this.ocrData.nom || '')).trim();
      return full || '—';
    },
    cylindreeDisplay() {
      var value = this.parseCylindree(this.ocrData.cylindree);
      if (value == null) return String(this.ocrData.cylindree || '').trim();
      return `${value} cm3`;
    },
    manualMarques() {
      var seen = {};
      var list = [];
      for (var i = 0; i < Marque2roues.length; i++) {
        var item = Marque2roues[i];
        if (item && item.Marque && !seen[item.Marque]) {
          // On retire "AUTRE" de la liste car gere par l'option dediee "Autre (non liste)"
          if (String(item.Marque).trim().toUpperCase() === 'AUTRE') continue;
          seen[item.Marque] = true;
          list.push(item.Marque);
        }
      }
      return list.sort();
    },
    manualModeles() {
      if (!this.ocrData.marque) return [];
      var seen = {};
      var list = [];
      for (var i = 0; i < Marque2roues.length; i++) {
        var item = Marque2roues[i];
        if (item && item.Marque === this.ocrData.marque && item.Type && !seen[item.Type]) {
          // On retire "AUTRE" du Type car gere par l'option dediee
          if (String(item.Type).trim().toUpperCase() === 'AUTRE') continue;
          seen[item.Type] = true;
          list.push(item.Type);
        }
      }
      return list.sort();
    }
  },
  watch: {
    step(newStep) {
      if (newStep === 3 && !this.customDateEffet) {
        this.customDateEffet = this.minDateEffet;
      }
      if (newStep === 3) {
        this.loadTarification();
      }
    }
  },
  methods: {
    SaveUser2roues(vehicules, userid) {
      const ListAuto = [];
      ListAuto.push(vehicules);
      Api()
        .post('flette/addarray', { car: ListAuto, user: userid })
        .then((result) => {
          console.log('vehicule enregistré');
        })
        .catch((err) => {
          console.log(err);
        });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('fr-FR', options);
    },
    matchSelectValue(value, list) {
      if (value === '' || value == null) return '';
      const str = String(value).trim();
      for (let i = 0; i < list.length; i++) {
        if (String(list[i]) === str) return list[i];
      }
      return '';
    },
    parseCylindree(value) {
      if (value == null) return null;
      var str = String(value).replace(',', '.');
      var match = str.match(/(\d+(\.\d+)?)/);
      if (!match) return null;
      var n = Number(match[1]);
      if (isNaN(n)) return null;
      return Math.round(n);
    },
    async loadTarification() {
      if (this.tarificationLoading || this.tarification) return;
      this.tarificationLoading = true;
      this.tarificationError = null;
      try {
        var agentCode = this.isAgentValid ? this.agentCode : null;
        const result = await TiaKTiaKService.getTarification(agentCode);
        this.tarification = result;
      } catch (error) {
        this.tarificationError = error;
      } finally {
        this.tarificationLoading = false;
      }
    },
    prevStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    async nextStep() {
      if (this.step === 2 && !this.showInsuranceWarning) {
        await this.handlePlateVerification();
      } else {
        this.step++;
        this.showInsuranceWarning = false;
      }
    },
    async handlePlateVerification() {
      // Vérification Diatoli temporairement désactivée (API en erreur)
      this.insuranceFound = null;
      this.step++;
      return;
      /*
      const immatriculation = this.ocrData.immatriculation;
      if (!immatriculation) {
        this.step++;
        return;
      }

      this.isLoading = true;
      this.loadingMessage = 'Vérification de l\'assurance en cours...';
      try {
        const result = await TiaKTiaKService.checkExistingInsurance(immatriculation);
        if (result && result.data && result.data.operationStatus === 'SUCCESS') {
          this.insuranceFound = result.data;

          // Si chevauchement > 1 jour avec aujourd'hui, on informe l'user des maintenant
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const echeance = new Date(this.insuranceFound.data.dateEcheance);
          echeance.setHours(0, 0, 0, 0);
          const diffDays = Math.round((echeance.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays > 1) {
            this.showInsuranceWarning = true;
          }
        } else {
          this.insuranceFound = null;
        }
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          if (status === 400) {
            console.error('Erreur 400: Immatriculation manquante pour la vérification.');
          } else if (status === 502) {
            console.error('Erreur 502: Service tiers de vérification indisponible.');
          } else {
            console.error(`Erreur ${status} lors de la vérification de l'assurance.`);
          }
        } else {
          console.error('Erreur lors de la vérification de l\'assurance:', error);
        }
        this.insuranceFound = null;
      } finally {
        this.isLoading = false;
        this.step++;
      }
      */
    },
    evaluateAndCreateDevis() {
      if (
        !this.insuranceFound ||
        !this.insuranceFound.data ||
        !this.insuranceFound.data.dateEcheance
      ) {
        this.createDevis();
        return;
      }
      const echeance = new Date(this.insuranceFound.data.dateEcheance);
      echeance.setHours(0, 0, 0, 0);
      const userDate = new Date(this.customDateEffet);
      userDate.setHours(0, 0, 0, 0);
      const diffDays = Math.round(
        (echeance.getTime() - userDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffDays < 0) {
        // Date saisie posterieure a l'echeance : aucun chevauchement
        this.createDevis();
      } else if (diffDays <= 1) {
        // Chevauchement <= 24h : decalage automatique silencieux
        this.customDateEffet = this.proposedDateEffet;
        this.$toasted.show(
          `Date d'effet décalée automatiquement au ${this.formatDate(this.customDateEffet)} (chevauchement avec assurance en cours).`,
          this.ToastTabSucess
        );
        this.createDevis();
      } else {
        // Ecart > 1 jour : modale de proposition (depuis le bouton "Obtenir le devis")
        this.pendingDevisCreation = true;
        this.showInsuranceWarning = true;
      }
    },
    confirmContinue() {
      if (this.proposedDateEffet) {
        this.customDateEffet = this.proposedDateEffet;
      }
      this.showInsuranceWarning = false;
      // Si la modale a ete ouverte au moment de creer le devis, on relance la creation.
      // Sinon (modale d'info anticipee au step 2->3), on laisse l'user finir l'etape 3.
      if (this.pendingDevisCreation) {
        this.pendingDevisCreation = false;
        this.createDevis();
      }
    },
    cancelInsuranceWarning() {
      this.showInsuranceWarning = false;
      if (this.pendingDevisCreation) {
        // Modale ouverte depuis "Obtenir le devis" : on reste sur l'etape 3
        this.pendingDevisCreation = false;
        return;
      }
      // Modale d'info anticipee au step 2->3 : l'user interrompt le parcours, reset complet
      this.step = 1;
      this.nom = '';
      this.prenom = '';
      this.phoneNumber = '';
      this.vehicleMode = 'ocr';
      this.proprietaireSameAsBeneficiaire = false;
      this.resetVehicleData();
      this.carteGriseRecto = null;
      this.carteGriseVerso = null;
      this.insuranceFound = null;
      this.customDateEffet = null;
      this.selectedDuration = null;
    },
    resetVehicleData() {
      this.ocrData = {
        immatriculation: '',
        numeroChassis: '',
        nom: '',
        prenom: '',
        marque: '',
        modele: '',
        cylindree: '',
        carburant: '',
        puissanceFiscale: '',
        nombrePlaces: '2',
        dateCirculation: '',
        puissance_fiscale: null,
        nombre_places: null,
        type_carburant: '',
        date_premiere_mise_en_circulation: '',
        volumetrie2_roues: null
      };
      this.ocrSubmitted = false;
      this.isEditingOcr = false;
      this.ocrError = null;
      this.isCustomVehicle = false;
      this.vehiculeImmatricule = 'oui';
    },
    setVehicleMode(mode) {
      if (mode === this.vehicleMode) return;
      this.vehicleMode = mode;
      this.resetVehicleData();
      if (mode === 'manual') {
        if (this.canUseSameAsBeneficiaire) {
          this.proprietaireSameAsBeneficiaire = true;
          this.applyProprietaireFromBeneficiaire();
        } else {
          this.proprietaireSameAsBeneficiaire = false;
        }
      } else {
        this.proprietaireSameAsBeneficiaire = false;
        this.carteGriseRecto = null;
        this.carteGriseVerso = null;
      }
    },
    applyProprietaireFromBeneficiaire() {
      if (this.proprietaireSameAsBeneficiaire && this.canUseSameAsBeneficiaire) {
        this.ocrData.nom = this.nom || '';
        this.ocrData.prenom = this.prenom || '';
      }
    },
    onProprietaireToggle() {
      if (this.proprietaireSameAsBeneficiaire) {
        this.applyProprietaireFromBeneficiaire();
      } else {
        this.ocrData.nom = '';
        this.ocrData.prenom = '';
      }
    },
    onVehiculeImmatriculeChange() {
      if (this.vehiculeImmatricule === 'oui') {
        this.ocrData.numeroChassis = '';
      } else {
        this.ocrData.immatriculation = '';
      }
    },
    onPhoneInput() {
      this.phoneNumber = sanitizePhoneNumber(this.phoneNumber, this.countryCode);
    },
    validateImmatriculation() {
      // Format accepte: XX-XXX-XX ou XX-XXXX-XX (alphanum + tirets)
      var regex = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{3,4}-[A-Za-z0-9]{2}$/;
      if (!this.ocrData.immatriculation) return;
      if (!regex.test(this.ocrData.immatriculation)) {
        this.ocrData.immatriculation = this.ocrData.immatriculation.replace(/[^A-Za-z0-9-]/g, '');
      }
    },
    validateNumeroChassis() {
      var regex = /^[A-Za-z0-9-]+$/;
      if (!this.ocrData.numeroChassis) return;
      if (!regex.test(this.ocrData.numeroChassis)) {
        this.ocrData.numeroChassis = this.ocrData.numeroChassis.replace(/[^A-Za-z0-9-]/g, '');
      }
    },
    isAutreSentinel(val) {
      if (val == null) return false;
      var v = String(val).trim().toUpperCase();
      return v === '__AUTRE__' || v === 'AUTRE';
    },
    onMarqueSelect(val) {
      if (this.isAutreSentinel(val)) {
        this.isCustomVehicle = true;
        this.ocrData.marque = '';
        this.ocrData.modele = '';
        return;
      }
      this.ocrData.marque = val;
      this.ocrData.modele = '';
    },
    onModeleSelect(val) {
      if (this.isAutreSentinel(val)) {
        this.isCustomVehicle = true;
        this.ocrData.modele = '';
        return;
      }
      this.ocrData.modele = val;
    },
    useStandardVehicle() {
      this.isCustomVehicle = false;
      this.ocrData.marque = '';
      this.ocrData.modele = '';
    },
    forceCustomVehicle() {
      this.isCustomVehicle = true;
      this.ocrData.marque = '';
      this.ocrData.modele = '';
    },
    validateManualVehicle() {
      if (this.proprietaireSameAsBeneficiaire) {
        this.applyProprietaireFromBeneficiaire();
      }
      if (!this.isOcrDataValid) {
        this.$toasted.show('Veuillez renseigner tous les champs obligatoires.', this.ToastTabError);
        return;
      }
      if (!this.checkVehicleIdentifierFormat()) return;
      this.ocrSubmitted = true;
      this.isEditingOcr = false;
    },
    checkVehicleIdentifierFormat() {
      if (this.vehiculeImmatricule === 'oui') {
        var regex = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{3,4}-[A-Za-z0-9]{2}$/;
        if (!regex.test(this.ocrData.immatriculation || '')) {
          this.$toasted.show(
            "Format d'immatriculation invalide (attendu : XX-XXX-XX ou XX-XXXX-XX).",
            this.ToastTabError
          );
          return false;
        }
      } else {
        var chassis = this.ocrData.numeroChassis || '';
        if (chassis.length < 17) {
          this.$toasted.show(
            'Le numéro de châssis doit comporter au moins 17 caractères.',
            this.ToastTabError
          );
          return false;
        }
      }
      if (this.ocrData.dateCirculation && this.ocrData.dateCirculation > this.todayDate) {
        this.$toasted.show(
          "La date de 1ère mise en circulation ne peut pas être postérieure à aujourd'hui.",
          this.ToastTabError
        );
        return false;
      }
      return true;
    },
    triggerUpload(type, isCamera) {
      const refName = isCamera
        ? `cameraInput${type.charAt(0).toUpperCase() + type.slice(1)}`
        : `fileInput${type.charAt(0).toUpperCase() + type.slice(1)}`;
      const input = this.$refs[refName];
      if (input) {
        input.click();
      }
    },
    removeFile(type) {
      if (type === 'recto') {
        this.carteGriseRecto = null;
      } else if (type === 'verso') {
        this.carteGriseVerso = null;
      }
    },
    handleFileUpload(event, type) {
      const file = event.target.files[0];
      if (!file) return;

      const validation = CarteGriseOcrService.validateFile(file);
      if (!validation.valid) {
        return;
      }

      if (type === 'recto') {
        this.carteGriseRecto = file;
      } else if (type === 'verso') {
        this.carteGriseVerso = file;
      }
    },
    async submitOcr() {
      this.loadingMessage = 'Analyse de la carte grise en cours...';
      this.isLoading = true;
      this.ocrError = null;

      try {
        if (!this.carteGriseRecto || !this.carteGriseVerso) {
          this.ocrError = this.getOcrErrorMessage('BOTH_REQUIRED');
          this.isLoading = false;
          return;
        }
        const data = await CarteGriseOcrService.scanCarteGrise(
          this.carteGriseRecto,
          this.carteGriseVerso
        );

        // Fusionne les données OCR avec la structure de ocrData
        this.ocrData = { ...this.ocrData, ...data };
        if (
          this.ocrData.nombrePlaces === 2 ||
          this.ocrData.nombrePlaces === '2' ||
          this.ocrData.puissanceFiscale === 2 ||
          this.ocrData.puissanceFiscale === 1
        ) {
          const cylindreeValue = this.parseCylindree(this.ocrData.cylindree);
          if (cylindreeValue != null && cylindreeValue > 250) {
            this.$toasted.show(
              'Cylindrée non éligible : elle ne doit pas dépasser 250 cm3.',
              this.ToastTabError
            );
            return;
          }
          // Normaliser pour matcher les <option> (OCR peut renvoyer number vs string)
          this.ocrData.puissanceFiscale = this.matchSelectValue(
            this.ocrData.puissanceFiscale,
            this.puissanceFiscaleItems
          );
          this.ocrData.nombrePlaces = this.matchSelectValue(
            this.ocrData.nombrePlaces,
            this.nombrePlacesItems
          );
          this.isEditingOcr = false;
          this.ocrSubmitted = true; // Affiche les données OCR dans le step 2
        } else {
          this.$toasted.show('Carte grise non valide pour les 2 roues', this.ToastTabError);
        }
      } catch (error) {
        console.error('Erreur OCR:', error);
        this.ocrError = this.getOcrErrorMessage(error.message || 'GENERIC_ERROR');
      } finally {
        this.isLoading = false;
      }
    },

    getOcrErrorMessage(code) {
      const messages = {
        NO_FILE: 'Veuillez sélectionner un fichier.',
        INVALID_TYPE: 'Le format du fichier est invalide. Seuls JPEG, PNG et PDF sont acceptés.',
        FILE_TOO_LARGE: 'Le fichier est trop volumineux. La taille maximale est de 5 Mo.',
        OCR_INCOMPLETE_DATA:
          "L'analyse n'a pas pu extraire toutes les données. Veuillez réessayer avec une meilleure image.",
        BOTH_REQUIRED: 'Veuillez fournir le recto et le verso de la carte grise.',
        GENERIC_ERROR: "Une erreur est survenue lors de l'analyse. Veuillez réessayer."
      };
      return messages[code] || messages['GENERIC_ERROR'];
    },

    selectDuration(duration) {
      this.selectedDuration = duration;
    },
    async createDevis() {
      this.loadingMessage = 'Création du devis en cours...';
      this.isLoading = true;
      try {
        // Si l'utilisateur a choisi "Non" en mode manuel, le numéro de châssis remplace
        // le numéro d'immatriculation au niveau du payload.
        const ocrPayload = Object.assign({}, this.ocrData);
        if (this.vehicleMode === 'manual' && this.vehiculeImmatricule === 'non') {
          ocrPayload.immatriculation = this.ocrData.numeroChassis;
        }

        const devisData = {
          client: {
            nom: this.nom,
            prenom: this.prenom,
            telephone: this.countryCode + sanitizePhoneNumber(this.phoneNumber, this.countryCode)
          },
          beneficiaire: {
            nom: this.ocrData.nom,
            prenom: this.ocrData.prenom || '-'
          },
          ocr_data: ocrPayload,
          duree: this.selectedDuration,
          dateEffet: this.customDateEffet,
          codeAgent: this.agentCode,
          assureurCode: 'ASS',
          plateforme: 'Front-Aywa'
        };

        // const devisData = {
        //   nom: this.nom,
        //   prenom: this.prenom,
        //   telephone: this.phoneNumber,
        //   beneficiaire: {
        //     nom: this.ocrData.nom,
        //     prenom: this.ocrData.prenom || '-'
        //   },
        //   ocr_data: ocrPayload,
        //   duree: this.selectedDuration,
        //   dateEffet: this.customDateEffet,
        //   codeAgent: this.agentCode
        // };

        const result = await TiaKTiaKService.createDevis(devisData);
        this.devis = result.result; // Stocke la réponse du devis
        let vehicules = this.devis.devis[0].vehicules[0];
        let user = this.devis.devis[0].userid;
        // Sauvegarder la flette
        this.SaveUser2roues(vehicules, user);
        //Passer dans step 4 pour afficher le devis et les options de paiement
        this.step = 4;
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error)) ||
          error.message ||
          'La création du devis a échoué';
        this.$toasted.show(message, this.ToastTabError);
      } finally {
        this.isLoading = false;
      }
    },

    async payWithWave() {
      this.loadingMessage = 'Initialisation du paiement Wave...';
      this.isLoading = true;
      try {
        const paymentData = {
          numeroDevis: this.devis.devis[0].numeroDevis,
          montant: this.devis.devis[0].prime_ttc,
          codeAgent: this.agentCode
        };
        const result = await TiaKTiaKService.initiateWavePayment(paymentData);
        if (result.wave_launch_url) {
          window.location.href = result.wave_launch_url;
        } else {
          throw new Error("L'URL de paiement Wave n'a pas été reçue.");
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation du paiement Wave:", error);
        this.isLoading = false;
      }
    },
    payWithOrangeMoney() {
      this.omPhoneNumber = '';
      this.omOtp = '';
      this.showOrangeMoneyModal = true;
    },
    async submitOrangeMoneyPayment() {
      this.loadingMessage = 'Initialisation du paiement Orange Money...';
      this.isLoading = true;
      this.showOrangeMoneyModal = false;

      try {
        const paymentData = {
          numeroDevis: this.devis.devis[0].numeroDevis,
          montant: this.devis.devis[0].prime_ttc,
          telephone: this.omPhoneNumber,
          otp: this.omOtp,
          codeAgent: this.agentCode
        };
        await TiaKTiaKService.payWithOrangeMoneyTiaTchak(paymentData);
        this.startPollingForContrat(paymentData.numeroDevis);
      } catch (error) {
        console.error("Erreur lors de l'initialisation du paiement Orange Money:", error);
        this.isLoading = false;
      }
    },
    async refreshContratDetails() {
      if (!this.devis || !this.devis.devis || !this.devis.devis[0]) return;

      this.loadingMessage = 'Récupération des talons du contrat...';
      this.isLoading = true;
      try {
        const numeroDevis = this.devis.devis[0].numeroDevis;
        const details = await TiaKTiaKService.getContratDetails({ numeroDevis: numeroDevis });
        if (details && details.contrat) {
          this.contratDetails = details;
          this.$toasted.show('Détails mis à jour avec succès', this.ToastTabSucess);
        }
      } catch (error) {
        console.error('Erreur lors de la réactualisation:', error);
        this.$toasted.show(
          'Impossible de récupérer les détails pour le moment',
          this.ToastTabError
        );
      } finally {
        this.isLoading = false;
      }
    },
    share(method) {
      if (!this.contratDetails) return;

      const attestationLink = this.contratDetails.attestation.linkAttestation;
      const summary = `Voici votre attestation d'assurance : ${attestationLink}`;

      switch (method) {
        case 'pdf':
          window.open(attestationLink, '_blank');
          break;
        case 'carte_brune':
          window.open(this.contratDetails.attestation.linkCarteBrune, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(summary)}`, '_blank');
          break;
        case 'sms':
          window.open(`sms:?body=${encodeURIComponent(summary)}`, '_blank');
          break;
      }
    },

    startPollingForContrat(numeroDevis) {
      this.loadingMessage = 'En attente de la confirmation du paiement...';
      this.isLoading = true;

      const poll = async () => {
        try {
          const details = await TiaKTiaKService.getContratDetails({ numeroDevis: numeroDevis });
          // Affiche la réponse de l'API
          if (details && details.contrat) {
            // Condition à ajuster après analyse des logs
            this.contratDetails = details;
            this.stopPolling();
            // Reste à step 3 pour afficher la confirmation
          }
        } catch (error) {
          console.warn('Vérification du contrat en cours, paiement non confirmé pour le moment.');
        }
      };

      // Vérifie toutes les 8 secondes
      this.pollingIntervalId = setInterval(poll, 5000);

      // Arrête la vérification après 5 minutes
      this.pollingTimeoutId = setTimeout(() => {
        this.stopPolling();
      }, 300000); // 5 minutes
    },

    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
      if (this.pollingTimeoutId) {
        clearTimeout(this.pollingTimeoutId);
        this.pollingTimeoutId = null;
      }
      this.isLoading = false;
    },
    resubscribe() {
      this.step = 1;
      this.nom = '';
      this.prenom = '';
      this.phoneNumber = '';
      this.carteGriseRecto = null;
      this.carteGriseVerso = null;
      this.ocrSubmitted = false;
      this.ocrData = {
        immatriculation: '',
        marque: '',
        modele: '',
        puissance_fiscale: null,
        nombre_places: null,
        type_carburant: '',
        date_premiere_mise_en_circulation: '',
        volumetrie2_roues: null
      };
      this.ocrError = null;
      this.selectedDuration = null;
      this.isLoading = false;
      this.loadingMessage = '';
      this.devis = null;
      this.contratDetails = null;
      this.customDateEffet = null;
      this.insuranceFound = null;
      this.showInsuranceWarning = false;
      this.showOrangeMoneyModal = false;
      this.omPhoneNumber = '';
      this.omOtp = '';
      this.stopPolling();
    },
    resetOcr() {
      this.ocrSubmitted = false;
      this.ocrError = null;
      this.isEditingOcr = false;
      this.carteGriseRecto = null;
      this.carteGriseVerso = null;
    },
    toggleEditOcr() {
      if (this.vehicleMode === 'ocr') {
        this.proprietaireSameAsBeneficiaire = false;
      }
      // Si la marque/modele OCR n'est pas dans les listes, on bascule en saisie libre
      // pour ne pas perdre la valeur extraite quand le select repasse a son option vide.
      const marqueOcr = this.ocrData.marque ? String(this.ocrData.marque).trim() : '';
      const modeleOcr = this.ocrData.modele ? String(this.ocrData.modele).trim() : '';
      const marqueInList = marqueOcr && this.manualMarques.indexOf(marqueOcr) !== -1;
      const modeleInList = modeleOcr && this.manualModeles.indexOf(modeleOcr) !== -1;
      if ((marqueOcr && !marqueInList) || (modeleOcr && !modeleInList)) {
        this.isCustomVehicle = true;
      }
      this.isEditingOcr = true;
    },
    cancelEditOcr() {
      this.isEditingOcr = false;
    },
    confirmEditOcr() {
      if (this.proprietaireSameAsBeneficiaire) {
        this.applyProprietaireFromBeneficiaire();
      }
      if (!this.isOcrDataValid) {
        this.$toasted.show('Veuillez renseigner tous les champs obligatoires.', this.ToastTabError);
        return;
      }
      if (this.vehicleMode === 'manual' && !this.checkVehicleIdentifierFormat()) return;
      this.isEditingOcr = false;
    }
  },
  mounted() {
    if (this.agentCodeFromUrl) {
      this.agentCode = this.agentCodeFromUrl;
    } else {
      const pathParts = window.location.pathname.split('/');
      const aywaIndex = pathParts.indexOf('aywa');
      if (aywaIndex !== -1 && pathParts[aywaIndex + 1]) {
        this.agentCode = pathParts[aywaIndex + 1];
      }
    }
    // Normalisation en majuscules : /aywa/ag7180 doit fonctionner comme /aywa/AG7180
    if (this.agentCode) {
      this.agentCode = String(this.agentCode).trim().toUpperCase();
    }

    if (this.agentCode) {
      TiaKTiaKService.validateAgent(this.agentCode)
        .then((result) => {
          if (result && result.valid) {
            this.isAgentValid = true;
            this.agentInfo = result.agent || {};
            if (this.$toasted) {
              this.$toasted.show(
                `Agent validé : ${(this.agentInfo.prenom || '').trim()} ${(this.agentInfo.nom || '').trim()}`.trim(),
                this.ToastTabSucess
              );
            }
          } else {
            this.isAgentValid = false;
            this.agentInfo = {};
            if (this.$toasted) {
              this.$toasted.show(
                (result && result.message) || 'Code agent invalide',
                this.ToastTabError
              );
            }
          }
        })
        .catch(() => {
          this.isAgentValid = false;
          this.agentInfo = {};
        })
        .finally(() => {
          this.agentValidationDone = true;
          this.loadTarification();
        });
    } else {
      // Pas de codeAgent dans l'URL : parcours public, pas de validation a faire
      this.agentValidationDone = true;
      this.loadTarification();
    }
  }
};
</script>

<style scoped>
::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  font-size: 12px;
}
.pro-form-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(13, 78, 120, 0.15);
  padding: 20px;
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  position: relative;
}

.badge {
  font-size: 12px;
  color: #0d4e78;
  background: rgba(13, 78, 120, 0.08);
  border: 1px solid rgba(13, 78, 120, 0.14);
  padding: 6px 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
}

@media (max-width: 768px) {
  .badge {
    font-size: 11px;
    padding: 6px 8px;
  }
}

.pro-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(13, 78, 120, 0.12);
  margin-bottom: 16px;
}

.header-left {
  display: flex;
  align-items: center;
}

@media (max-width: 600px) {
  .pro-form-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .header-right {
    width: 100%;
  }
  .agent-badge {
    width: 100%;
    justify-content: center;
    max-width: 100%;
  }
  .agent-badge span {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.titles .title {
  margin: 0;
  font-size: 18px;
  color: #0d4e78;
  font-weight: 700;
  line-height: 1.2;
}

.titles .subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #515457;
}

.agent-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #f0f2f5;
  border-radius: 999px;
  color: #495057;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}
.agent-badge.is-valid {
  background-color: #ecfdf5;
  color: #065f46;
  border-color: #10b981;
}
.agent-badge i {
  color: #ffa409;
  font-size: 14px;
}
.agent-badge.is-valid i {
  color: #10b981;
}
.agent-badge strong {
  font-weight: 600;
}
.header-right {
  display: inline-flex; /* aligne badge et icône sur une ligne */
  align-items: center;
  gap: 10px; /* espace entre badge et X */
  white-space: nowrap; /* évite le retour à la ligne sur mobile */
}

@media (max-width: 768px) {
  .header-right {
    gap: 8px;
  }
}
.fields {
  margin-bottom: 10px;
}

.marque-col-with-cta {
  position: relative;
}
.cta-below-marque {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 6px;
}

/* Inputs de la ligne vehicule alignes en haut, peu importe la hauteur de la col marque (CTA en absolu). */
.vehicle-row {
  align-items: flex-start;
}
.vehicle-row .field-group {
  height: auto;
  justify-content: flex-start;
}
/* Hauteur min des labels pour aligner les inputs malgre les labels sur 2 lignes (ex: "Date de 1ere mise en circulation"). */
.vehicle-row .field-label {
  min-height: 32px;
  display: flex;
  align-items: flex-end;
}
@media (max-width: 768px) {
  .vehicle-row .field-label {
    min-height: 0;
  }
}
.proprio-row {
  margin-top: 80px;
}
.proprio-checkbox-row {
  margin-top: 56px;
}
@media (max-width: 600px) {
  .proprio-row {
    margin-top: 1rem;
  }
}
@media (max-width: 768px) {
  .cta-below-marque {
    position: static;
    margin-top: 10px;
    margin-bottom: 4px;
  }
  .btn-cta-nowrap {
    white-space: normal;
  }
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.col-sm-12 {
  flex: 1 1 100%;
}
.col-sm-6 {
  flex: 1 1 calc(50% - 12px);
  min-width: 260px;
}
.col-sm-3 {
  flex: 1 1 calc(25% - 12px);
  min-width: 140px;
}
.col-sm-4 {
  flex: 1 1 calc(30% - 12px);
  min-width: 200px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-end;
  height: 100%;
}

.field-label {
  font-weight: 600;
  color: #0d4e78;
  font-size: 13px;
  line-height: 1.2;
}

.phone-input-wrapper {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.phone-field-input {
  border-radius: 0 10px 10px 0 !important;
  border-left: none !important;
}

.field-input,
.field-select,
textarea.field-input {
  width: 100%;
  height: 45px;
  border: 1px solid rgba(13, 78, 120, 0.22);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  background: #fff;
  color: #1c2b36;
  font-size: 14px;
  margin-bottom: 0 !important;
  box-sizing: border-box;
}

.field-group label.field-label {
  margin-bottom: 0;
}

.field-input:focus,
.field-select:focus,
textarea.field-input:focus {
  border-color: #ffa409;
  box-shadow: 0 0 0 3px rgba(255, 164, 9, 0.18);
}

.agent-blocked-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  gap: 14px;
  color: #2b2b2b;
}
.agent-blocked-icon {
  font-size: 56px;
  color: #c0392b;
  margin-bottom: 10px;
}
.agent-blocked-screen h2 {
  font-size: 22px;
  margin: 0;
}
.agent-blocked-screen p {
  margin: 0;
  max-width: 480px;
  font-size: 14px;
  color: #555;
}
.agent-blocked-screen .btn-primary-modern {
  margin-top: 12px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

/* New Upload Styles */
.upload-box-wrapper {
  background: #f8fafc;
  border: 2px dashed rgba(13, 78, 120, 0.2);
  border-radius: 12px;
  padding: 15px;
  transition: all 0.3s ease;
}

.upload-box-wrapper:hover {
  border-color: #ffa409;
  background: #fff;
}

.upload-options {
  display: flex;
  gap: 10px;
}

.btn-upload-opt {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #0d4e78;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-upload-opt i {
  font-size: 18px;
  color: #ffa409;
}

.btn-upload-opt.camera {
  display: none; /* Masqué par défaut sur desktop */
}

.btn-upload-opt:hover {
  border-color: #ffa409;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.upload-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #ecfdf5;
  border-radius: 8px;
  border: 1px solid #10b981;
}

.file {
  width: 500px;
  margin-bottom: 0px;
  margin-left: 0%;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.file-name {
  font-size: 13px;
  color: #065f46;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.btn-remove-file {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-remove-file:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* Mode switch (OCR <-> Manual) */
.mode-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 18px 0 10px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.mode-divider::before,
.mode-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(13, 78, 120, 0.12);
}
.mode-divider span {
  padding: 0 14px;
}

.mode-switch-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 0 4px;
}
.mode-switch-text {
  margin: 0;
  font-size: 13px;
  color: #515457;
}
.btn-link-mode {
  background: none;
  border: none;
  color: #ffa409;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.2s ease;
}
.btn-link-mode:hover {
  background: rgba(255, 164, 9, 0.08);
}

.btn-link-mode-back {
  background: none;
  border: none;
  color: #0d4e78;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 6px;
}
.btn-link-mode-back:hover {
  color: #ffa409;
}

.mode-switch-cta-back {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 0 4px;
}
.btn-link-mode-back-cta {
  background: none;
  border: none;
  color: #ffa409;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.2s ease;
}
.btn-link-mode-back-cta:hover {
  background: rgba(255, 164, 9, 0.08);
}

.checkbox-inline-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #0d4e78;
  font-weight: 500;
  cursor: pointer;
  margin: 0 0 6px;
}
.checkbox-inline-row input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #ffa409;
}

.radio-inline-group {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.radio-inline-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0d4e78;
  font-weight: 500;
  cursor: pointer;
  margin: 0;
}
.radio-inline-row input[type='radio'] {
  width: 16px;
  height: 16px;
  accent-color: #ffa409;
}

.btn-link-inline {
  margin-left: 6px;
  padding: 0;
  background: transparent;
  border: 0;
  color: #ffa409;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-link-inline:hover {
  text-decoration: underline;
}

.btn-cta-nowrap {
  white-space: nowrap;
}

.field-section-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

@media (max-width: 600px) {
  .upload-options {
    flex-direction: column;
    gap: 12px;
  }

  .btn-upload-opt {
    padding: 15px;
    font-size: 14px;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    border: 2px solid #e2e8f0;
  }

  .btn-upload-opt.camera {
    display: flex; /* Affiché uniquement sur mobile */
    background: #fff8ed;
    border-color: #ffa409;
  }

  .btn-upload-opt i {
    font-size: 20px;
  }
}

.success-alert {
  border-color: rgba(13, 78, 120, 0.22) !important;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  width: 100%;
  flex: 1;
}

.required-asterisk {
  color: red;
}

.next-btn-active {
  /* background: #ffa409; */
  color: #ffa409;
  font-weight: 500;
  /* border-color: #ffa409; */
}

.step2-actions {
  justify-content: space-between;
}

.back-btn {
  /* background-color: #f5f5f5; */
  color: #333;
}
.actions-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.actions-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}
/* ============== Ancien css*/
.devis-details p {
  font-size: 1.3rem;
  color: #333;
}
.devis-details .price {
  font-size: 2rem;
}

.aywa-container {
  max-width: 1000px;
  margin: 2rem auto;
  font-family: 'Poppins', sans-serif;
  color: #333;
}

.card {
  background-color: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.back-btn {
  margin-top: -2px !important;
  background: none;
  /*border: 1px solid #e0e0e0;*/

  cursor: pointer;
  color: #555;
  transition: all 0.3s ease;
}

/*.back-btn:hover {
  background-color: #f5f5f5;
  border-color: #ccc;
}*/

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

/* Stepper iconographique (mockup parcours-identite) */
.stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 1.5rem 0.5rem 2.4rem;
}
.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffa409;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  position: relative;
  box-shadow: 0 2px 8px rgba(255, 164, 9, 0.35);
  flex-shrink: 0;
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease;
}
.step-circle--inactive {
  background: #f3f4f6;
  color: #9ca3af;
  box-shadow: none;
}
.step-circle__label {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  font-size: 10px;
  color: #6b7280;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}
.step-line {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  transition: background 0.3s ease;
}
.step-line--active {
  background: #ffa409;
}

@media (max-width: 600px) {
  .stepper {
    gap: 6px;
    margin: 1rem 0.25rem 1.8rem;
  }
  .step-circle {
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
  .step-circle__label {
    font-size: 8px;
    margin-top: 4px;
  }
}
.card-body {
  padding: 0rem 2.5rem;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
}

.step-title-main {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.step-description {
  font-size: 1rem;
  color: #777;
}

.form-row {
  display: flex;
  gap: 1.5rem;
}

.form-row .form-group {
  flex: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
}

.form-control-modern {
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-control-modern:focus {
  outline: none;
  border-color: #ffa409;
  box-shadow: 0 0 0 2px rgba(0, 165, 165, 0.1);
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1.5rem;
  /*//background-color: #f9f9f9;*/
  border-top: 1px solid #fff;
  gap: 1rem;
}

.btn-primary-modern,
.btn-secondary-modern {
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-primary-modern {
  background-color: #ffa409;
  color: #fff;
}

.btn-primary-modern:hover {
  background-color: #008a8a;
}

.btn-primary-modern:disabled {
  background-color: #d3d3d3;
  cursor: not-allowed;
}

.btn-secondary-modern {
  background-color: #e0e0e0;
  color: #333;
}

.btn-secondary-modern:hover {
  background-color: #d1d1d1;
}

/* Upload Section */
.upload-section {
  display: flex;
  gap: 1rem;
  justify-content: center;
}
.upload-box {
  border: 2px dashed #e0e0e0;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}
.upload-box:hover {
  border-color: #ffa409;
  background-color: #f7fdfd;
}
.upload-label {
  color: #555;
  font-weight: 500;
}
.file-input {
  display: none;
}

/* Duration Options consolidated at the end of file */

/* Payment Options */
.payment-options {
  text-align: center;
}
.payment-buttons {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1rem;
}
.payment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
  color: #0a0a0a;
}
.payment-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}
.payment-btn img {
  height: 40px;
}

/* Devis Summary */
.devis-summary,
.confirmation-summary {
  background-color: #f9f9f9;
  border-radius: 10px;
  padding: 1.5rem;
}
.insurer-logo {
  max-height: 40px;
  margin-bottom: 1rem;
}
.price {
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 1rem;
  color: #ffa409;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}
.loading-spinner {
  text-align: center;
  font-size: 1.2rem;
}
.loading-spinner i {
  font-size: 3rem;
  color: #ffa409;
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  padding: 2rem;
  border-radius: 15px;
  width: 90%;
  max-width: 450px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

/* Warning Modal specific styles */
.warning-modal {
  text-align: center;
}
.warning-modal .modal-icon {
  font-size: 3rem;
  color: #ffa409;
  margin-bottom: 1rem;
}
.warning-modal h3 {
  color: #0d4e78;
  margin-bottom: 1rem;
}
.warning-modal p {
  color: #515457;
  line-height: 1.5;
}
.warning-modal .modal-question {
  margin-top: 1rem;
  font-weight: 600;
}
</style>

<style scoped>
/* Form & Button Styles */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #495057;
  font-size: 15px;
}

.form-control-modern {
  display: block;
  width: 100%;
  padding: 12px 16px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 8px;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
}

.form-control-modern:focus {
  color: #212529;
  background-color: #fff;
  border-color: #3d84f7;
  outline: 0;
  box-shadow: 0 0 0 4px rgba(61, 132, 247, 0.15);
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn-primary-modern,
.btn-secondary-modern {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary-modern {
  background-color: #ffa409;
  color: #fff;
}

.btn-primary-modern:hover {
  background-color: #e89508;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-secondary-modern {
  background-color: #f0f2f5;
  color: #495057;
}

.btn-secondary-modern:hover {
  background-color: #e2e6ea;
  transform: translateY(-2px);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.modal-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  text-align: left;
}

.modal-content h3 {
  text-align: center;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.modal-actions .btn-secondary {
  background-color: #6c757d;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  color: white;
}

.loading-spinner {
  text-align: center;
}

.loading-spinner .fa-spinner {
  font-size: 38px;
  margin-bottom: 15px;
}

.loading-spinner p {
  font-size: 16px;
}

.devis-summary {
  background-color: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  text-align: left;
}

.insurer-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.insurer-logo {
  width: 50px;
  height: 50px;
  margin-right: 15px;
}

.devis-details p {
  margin: 5px 0;
}

.price {
  margin-top: 15px;
  font-size: 1.2em;
  font-weight: bold;
}

.price span {
  color: #007bff;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

label {
  display: block;
  margin-bottom: 5px;
}

input[type='date'] {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  margin-bottom: 10px;
}

input[type='tel'],
input[type='text'],
input[type='number'] {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  margin-bottom: 10px;
}

/*button {
  padding: 12px 25px;
  font-size: 18px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}*/

button:disabled {
  /*//background-color: #ccc;*/
  cursor: not-allowed;
}

button .fa-spinner {
  margin-right: 8px;
}

.step-indicator {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

.upload-section {
  margin: 20px 0;
}

.upload-box {
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 20px;
  cursor: pointer;
}

.upload-box:hover {
  border-color: #ffa409;
}

.upload-label {
  font-size: 18px;
  color: #333;
}

.file-input {
  display: none;
}

.error-message {
  color: #e53e3e;
  background-color: #fed7d7;
  border: 1px solid #fbb6b6;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  text-align: center;
}

.date-effet-wrapper {
  width: 100%;
  max-width: 280px;
  margin-right: auto;
  margin-left: 0;
  text-align: left;
}

.duration-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin: 20px 0;
}

@media (max-width: 480px) {
  .duration-options {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .duration-btn {
    padding: 12px;
  }
  .duration-label {
    font-size: 14px;
  }
  .duration-price {
    font-size: 13px;
  }
}

@media (max-width: 360px) {
  .duration-options {
    grid-template-columns: 1fr;
  }
}

.duration-btn {
  background-color: #f8fafc;
  color: #0d4e78;
  border: 2px solid #e2e8f0;
  padding: 15px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.duration-btn:hover {
  border-color: #ffa409;
  background-color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.duration-btn.selected {
  background-color: #fff8ed;
  color: #ffa409;
  border-color: #ffa409;
  box-shadow: 0 0 0 3px rgba(255, 164, 9, 0.1);
}

.duration-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.duration-label {
  font-size: 16px;
  font-weight: 600;
}

.duration-price {
  font-size: 14px;
  opacity: 0.8;
  font-weight: 500;
}

.duration-btn.selected .duration-price {
  color: #e89508;
}

/* ======== Nouveau design page paiement (Step 4) ======== */
.checkout-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 4px 14px rgba(13, 78, 120, 0.06);
  text-align: left;
  margin-top: 16px;
}

.checkout-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
}

.checkout-icon {
  font-size: 18px;
  color: #ffa409;
  background: rgba(255, 164, 9, 0.1);
  border-radius: 8px;
  padding: 8px 10px;
}

.checkout-title {
  font-size: 16px;
  font-weight: 700;
  color: #0d4e78;
  margin: 0;
}

.checkout-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkout-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 14px;
}

.checkout-label {
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}

.checkout-value {
  color: #0d4e78;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.checkout-sub {
  color: #6b7280;
  font-weight: 500;
}

.checkout-breakdown {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkout-breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
}

.checkout-breakdown-label {
  color: #6b7280;
  font-weight: 500;
}

.checkout-breakdown-value {
  color: #0d4e78;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.checkout-total {
  margin-top: 18px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fff8ed 0%, #fff 100%);
  border: 1px solid #ffa409;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.checkout-total-label {
  font-size: 14px;
  font-weight: 700;
  color: #0d4e78;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.checkout-total-amount {
  font-size: 26px;
  font-weight: 800;
  color: #ffa409;
  white-space: nowrap;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.checkout-total-currency {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-left: 2px;
}

.payment-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #0d4e78;
  margin: 0 0 12px;
  text-align: center;
}

.payment-options .payment-buttons {
  display: flex;
  gap: 14px;
  justify-content: center;
  margin-top: 6px;
  flex-wrap: wrap;
}

.payment-options .payment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 22px;
  border-radius: 14px;
  border: 1.5px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fff;
  min-width: 200px;
  font-size: 13px;
  font-weight: 600;
  color: #0d4e78;
}

.payment-options .payment-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.payment-options .payment-btn img {
  height: 44px;
  object-fit: contain;
}

.payment-btn-label {
  font-size: 14px;
  font-weight: 700;
  color: #0d4e78;
}

.payment-options .payment-btn.wave {
  border-color: #41c3ff;
}

.payment-options .payment-btn.wave:hover {
  background: #f0faff;
  border-color: #1ea7e6;
}

.payment-options .payment-btn.om {
  border-color: #ff7800;
}

.payment-options .payment-btn.om:hover {
  background: #fff5eb;
  border-color: #e16800;
}

.payment-security-note {
  margin-top: 14px;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.payment-security-note i {
  color: #22c55e;
}

@media (max-width: 600px) {
  .checkout-card {
    padding: 16px;
    border-radius: 12px;
  }
  .checkout-title {
    font-size: 15px;
  }
  .checkout-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  .checkout-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .checkout-value {
    text-align: left;
    font-size: 14px;
  }
  .checkout-breakdown {
    margin-top: 14px;
    padding-top: 12px;
  }
  .checkout-breakdown-row {
    font-size: 13px;
  }
  .checkout-total {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 16px 14px;
    text-align: center;
  }
  .checkout-total-label {
    font-size: 12px;
  }
  .checkout-total-amount {
    font-size: 30px;
  }
  .payment-section-title {
    font-size: 13px;
    margin-bottom: 10px;
  }
  .payment-options .payment-buttons {
    flex-direction: column;
    gap: 10px;
    margin-top: 0;
  }
  .payment-options .payment-btn {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    padding: 14px 18px;
    gap: 14px;
    min-width: 0;
  }
  .payment-options .payment-btn img {
    height: 36px;
  }
  .payment-security-note {
    margin-top: 10px;
    font-size: 11px;
  }
}

.confirmation-summary {
  background-color: #f0f8ff;
  border: 1px solid #add8e6;
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  text-align: left;
  font-size: 13px;
}

.confirmation-summary ul {
  list-style: none;
  padding: 0;
}

.confirmation-summary li {
  margin-bottom: 8px;
}

.sharing-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f2f5;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  color: #0d4e78;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sharing-options {
  text-align: center;
  margin-top: 2rem;
}

.share-buttons-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.share-btn {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
}

.share-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.share-btn i {
  font-size: 1.2rem;
  width: auto;
  text-align: center;
}

.share-btn.pdf {
  background-color: #d9534f;
}
.share-btn.whatsapp {
  background-color: #25d366;
}
.share-btn.sms {
  background-color: #f0ad4e;
}
</style>

<style scoped>
/* OCR Details Styles */
.ocr-details-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
  margin-bottom: 20px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  color: #0d4e78;
  font-weight: 600;
}

.btn-outline-primary {
  transition: all 0.3s;
  border: 1px solid #ffa409;
  background-color: transparent;
  color: #ffa409;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-outline-primary:hover {
  background: #ffa409 !important;
  color: #fff !important;
}

.btn-outline-primary:disabled {
  border-color: #d3d3d3;
  color: #d3d3d3;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .card-footer {
    padding: 1rem;
  }

  .actions.step2-actions {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .actions.step2-actions .btn-primary-modern,
  .actions.step2-actions .btn-outline-primary,
  .actions.step2-actions .wizard-btn.back-btn,
  .card-footer > .btn-primary-modern {
    width: 100% !important;
    margin: 0 !important;
    justify-content: center;
    padding: 0.7rem 1rem;
    font-size: 1.2rem;
    height: auto;
    min-height: 44px;
  }

  .actions-left,
  .actions-right {
    width: 100%;
    flex-direction: column;
    gap: 8px;
    margin-left: 0 !important;
  }

  .card-footer {
    flex-direction: column;
    gap: 10px;
  }

  /* Pour l'étape 2 spécifiquement, on veut le bouton Suivant en premier (en haut) */
  .actions.step2-actions {
    display: flex;
    flex-direction: column-reverse;
  }

  .actions.step2-actions .btn-primary-modern {
    order: -1;
    margin-bottom: 4px !important;
  }
}
</style>


